import {isLoggedIn} from '@github-ui/client-env'
import type {CommitNotices} from '@github-ui/diff-view-settings/types'
import {dismissUserNoticePath} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import type {CommitPayload} from '../types/commit-types'

/**
 * Hook to check if a specific notice has been dismissed.
 *
 * @param notice - The notice name to check
 * @returns true if the notice is dismissed or not found, false otherwise
 */
export function useIsNoticeDismissed(notice: CommitNotices) {
  const payload = useRoutePayload<CommitPayload>()
  const activeNotices = (payload?.userNotices ?? []).filter(n => !n.dismissed)

  return activeNotices.filter(activeNotice => activeNotice.name === notice).length === 0
}

/**
 * Hook to dismiss a user notice.
 *
 * @param notice - The notice name to dismiss
 * @returns Object containing dismissNotice function
 */
export function useDismissNotice(notice: CommitNotices) {
  const isNoticeDismissed = useIsNoticeDismissed(notice)

  const dismissNotice = useCallback(() => {
    // Don't dismiss the notice if the user is not logged in or the notice is already dismissed
    if (!isLoggedIn() || isNoticeDismissed) return

    verifiedFetch(dismissUserNoticePath({noticeName: notice}), {method: 'POST'})
  }, [isNoticeDismissed, notice])

  return {dismissNotice}
}
